/**
 * A small, reusable, accessible combobox (WAI-ARIA "combobox with list
 * popup" pattern) — Task 5's searchable ragam picker, but framework-free so
 * any screen could reuse it for another searchable field later.
 *
 * The caller owns the DOM (`input` + `listEl`) and their layout/CSS; this
 * module only sets the ARIA relationship between them and wires keyboard +
 * pointer behaviour. All cleanup rides on the caller's `AbortSignal` (the
 * same pattern `setup.ts`/`live.ts` use for their own listeners), so there's
 * no separate `destroy()` to remember to call.
 *
 * ARIA/keyboard contract:
 *  - `input[role=combobox]` with `aria-expanded`/`aria-controls`/
 *    `aria-activedescendant` kept in sync with the popup.
 *  - `listEl[role=listbox]` containing `li[role=option]` children, each with
 *    a stable id and `aria-selected` reflecting the active option.
 *  - ArrowDown/ArrowUp move (and wrap) the active option, opening the list
 *    first if it's closed.
 *  - Enter commits the active option (if any is active).
 *  - Escape closes the list without clearing the typed text.
 *  - Clicking an option commits it; visible focus stays on the input the
 *    whole time (mousedown on the list is prevented from blurring it).
 */

export interface CreateComboboxOptions<T> {
  input: HTMLInputElement;
  /** Rendered as the popup listbox — any empty container element. */
  listEl: HTMLElement;
  /** Cleanup hook: all listeners this call adds are removed when it aborts. */
  signal: AbortSignal;
  /** Given the current input text, return the (already ranked/filtered) items to show. */
  search(query: string): T[];
  /** Plain-text label for an item — becomes the input's value on selection. */
  label(item: T): string;
  /** Optional richer HTML for an option's content; defaults to the escaped label. */
  renderOption?(item: T): string;
  /** Called when the user commits a selection (Enter or click on an option). */
  onSelect(item: T): void;
  /** Screen-reader name for the input, since its visible `<label>` may describe a wider field group. */
  ariaLabel?: string;
  placeholder?: string;
}

function escapeHtml(s: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };
  return s.replace(/[&<>"']/g, (c) => map[c]!);
}

let nextListId = 0;

/** Wires the WAI-ARIA combobox pattern onto an existing `input` + `listEl` pair. */
export function createCombobox<T>(options: CreateComboboxOptions<T>): void {
  const { input, listEl, signal, search, label, onSelect } = options;
  const renderOption = options.renderOption ?? ((item: T) => escapeHtml(label(item)));

  let items: T[] = [];
  let activeIndex = -1;
  let open = false;

  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-haspopup', 'listbox');
  input.setAttribute('aria-expanded', 'false');
  if (!listEl.id) listEl.id = `combobox-list-${nextListId++}`;
  input.setAttribute('aria-controls', listEl.id);
  if (options.ariaLabel !== undefined) input.setAttribute('aria-label', options.ariaLabel);
  if (options.placeholder !== undefined) input.placeholder = options.placeholder;
  listEl.setAttribute('role', 'listbox');
  listEl.hidden = true;

  const optionId = (i: number): string => `${listEl.id}-opt-${i}`;

  function render(): void {
    listEl.innerHTML = items
      .map(
        (item, i) =>
          `<li id="${optionId(i)}" role="option" class="combobox-option"${
            i === activeIndex ? ' data-active="true"' : ''
          } aria-selected="${i === activeIndex}">${renderOption(item)}</li>`,
      )
      .join('');
  }

  function setOpen(next: boolean): void {
    open = next && items.length > 0;
    listEl.hidden = !open;
    input.setAttribute('aria-expanded', String(open));
    if (!open) {
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
    }
  }

  function setActive(i: number): void {
    if (items.length === 0) {
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
      return;
    }
    activeIndex = ((i % items.length) + items.length) % items.length;
    input.setAttribute('aria-activedescendant', optionId(activeIndex));
    render();
    listEl
      .querySelector(`#${CSS.escape(optionId(activeIndex))}`)
      ?.scrollIntoView({ block: 'nearest' });
  }

  function refresh(query: string): void {
    items = search(query);
    activeIndex = -1;
    render();
    setOpen(true);
  }

  function commit(i: number): void {
    const item = items[i];
    if (!item) return;
    input.value = label(item);
    setOpen(false);
    onSelect(item);
  }

  input.addEventListener('input', () => refresh(input.value), { signal });
  input.addEventListener('focus', () => refresh(input.value), { signal });

  input.addEventListener(
    'keydown',
    (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!open) refresh(input.value);
        else setActive(activeIndex + 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (!open) refresh(input.value);
        else setActive(activeIndex - 1);
      } else if (e.key === 'Enter') {
        if (open && activeIndex >= 0) {
          e.preventDefault();
          commit(activeIndex);
        }
      } else if (e.key === 'Escape') {
        if (open) {
          e.preventDefault();
          setOpen(false);
        }
      }
    },
    { signal },
  );

  // A plain blur would fire (and close the list) before a click on an option
  // registers, since mousedown blurs the input first. Deferring the close
  // and preventing default on the list's own mousedown both exist to let
  // that click land before the list disappears.
  input.addEventListener(
    'blur',
    () => {
      setTimeout(() => setOpen(false), 100);
    },
    { signal },
  );
  listEl.addEventListener('mousedown', (e) => e.preventDefault(), { signal });
  listEl.addEventListener(
    'click',
    (e) => {
      const li = (e.target as HTMLElement).closest('li[role="option"]');
      if (!li) return;
      const idx = Array.from(listEl.children).indexOf(li);
      if (idx >= 0) commit(idx);
    },
    { signal },
  );
}
